import React, { useRef, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { LogOut, Send } from 'lucide-react'
import { useStreamChat } from '../hooks/useStreamChat'
import ChatBubble from '../components/ChatBubble'
import ChatInput from '../components/ChatInput'
import AuthService from '../../../services/AuthService'

const NMPLogo = require('../../../Images/NMP.png')
const SkyLogo = require('../../../Images/skyangel.png')

interface Props {
  token: string
  user: { username: string; email: string; name?: string }
  onBack: () => void
}

const TextChatPage: React.FC<Props> = ({ token, user, onBack }) => {
  const { messages, isStreaming, sendMessage } = useStreamChat()
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = (text: string) => {
    sendMessage(text, token, 0, user.name || user.username)
  }

  const handleLogout = () => {
    AuthService.logout()
    onBack()
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackBtn onClick={() => window.location.href = '/interactive'}>← Volver</BackBtn>
          <LogoIcon>
            <img src={NMPLogo} alt="NMP" style={{ width: '50px', height: '50px', objectFit: 'contain' }} />
          </LogoIcon>
          <TitleGroup>
            <Title>Piedad IA</Title>
            <Subtitle>Chat de texto · Nacional Monte de Piedad</Subtitle>
          </TitleGroup>
        </HeaderLeft>
        <HeaderRight>
          <SkyLogoImg src={SkyLogo} alt="SkyAngel" />
          <LogoutBtn onClick={handleLogout}>
            <LogOut size={16} />
            Salir
          </LogoutBtn>
        </HeaderRight>
      </Header>

      <MessagesArea>
        {messages.length === 0 && (
          <EmptyState>
            <EmptyLogo>
              <img src={NMPLogo} alt="NMP" style={{ width: '80px', height: '80px', objectFit: 'contain', opacity: 0.6 }} />
            </EmptyLogo>
            <EmptyTitle>¿En qué puedo ayudarte?</EmptyTitle>
            <EmptyHint>Pregunta sobre servicios, GPS, reportes o cualquier tema del negocio</EmptyHint>
          </EmptyState>
        )}
        {messages.map(msg => (
          <ChatBubble key={msg.id} message={msg} />
        ))}
        {isStreaming && <StreamingDot>●●●</StreamingDot>}
        <div ref={bottomRef} />
      </MessagesArea>

      <ChatInput onSend={handleSend} disabled={isStreaming} />
    </Container>
  )
}

// Animations
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(135deg, #1a0008 0%, #0d0004 100%);
  color: #e2e8f0;
`

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #98012E, #CA7C8E, #98012E);
  background-size: 200% 200%;
  animation: ${gradientShift} 4s ease infinite;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const BackBtn = styled.button`
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.25); }
`

const LogoIcon = styled.div`
  width: 55px;
  height: 55px;
  background: linear-gradient(145deg, #ffffff, #f5f5f5);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const Title = styled.h1`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
`

const Subtitle = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SkyLogoImg = styled.img`
  height: 30px;
  opacity: 0.9;
`

const LogoutBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  &:hover { background: rgba(255, 255, 255, 0.25); }
`

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #3d1020; border-radius: 2px; }
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 0.75rem;
`

const EmptyLogo = styled.div`
  margin-bottom: 0.5rem;
`

const EmptyTitle = styled.h2`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.8);
`

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.4);
`

const StreamingDot = styled.div`
  color: #98012E;
  font-size: 1.2rem;
  padding-left: 0.5rem;
  animation: pulse 1s infinite;
  @keyframes pulse { 50% { opacity: 0.4; } }
`

export default TextChatPage
